<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import { useGlobalStore } from '@/stores/global';
import { useSourceStore } from '@/stores/source';
import type { UpdateSourceRequest } from '@/types/requests/sources/updateSourceRequest';
import type { Source } from '@/types/source';
import { maxLength, required } from '@/utils/validation';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
    source?: Omit<Source, '_id'> | null;
    organizationId?: string;
}>();

const emit = defineEmits(['closeDialog', 'update:modelValue']);

const isEditMode = computed(() => !!props.source);

const form = useTemplateRef('form');

const sourceStore = useSourceStore();
const globalStore = useGlobalStore();

const request = ref<UpdateSourceRequest>({} as UpdateSourceRequest);

const isDialogOpen = defineModel<boolean>();

watch(isDialogOpen, initialize);

onMounted(initialize);

function initialize() {
    if (isEditMode.value) request.value = { ...props.source } as UpdateSourceRequest;
    if (props.organizationId) request.value.organization = props.organizationId;
}

async function handleUpsert() {
    if (!form.value?.validate()) return;
    try {
        if (isEditMode.value) {
            await sourceStore.update(request.value);
            closeDialog();
            return;
        }

        await sourceStore.create(request.value);
        closeDialog();
    } catch (error) {
        console.error(error);
    }
}

function closeDialog() {
    request.value = {} as UpdateSourceRequest;
    emit('closeDialog');
}
</script>

<template>
    <Dialog v-model="isDialogOpen" :title="`Bron ${isEditMode ? 'bewerken' : 'aanmaken'}`" max-width="500" :loading="globalStore.isLoading">
        <v-form ref="form">
            <v-text-field v-model="request.name" required label="Naam" :rules="[required, maxLength(50)]" />
        </v-form>

        <template #actions>
            <v-spacer />
            <CustomButton class="mr-2" color="green" @click="handleUpsert" text="Bevestigen" />
            <CustomButton color="red" @click="closeDialog" text="Annuleren" />
        </template>
    </Dialog>
</template>
